import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  getNotifications,
  updateUser,
  markAsRead,
  resetPassword as resetPasswordService
} from '@/services/UserService'
import { useLocalStorage } from '@vueuse/core'
import { acceptHMRUpdate } from 'pinia'
import { useCurrentUser } from 'vuefire'
import { useApiHeaders } from '@/composables/apiHeaders'

export const useUserStore = defineStore('userStore', () => {
  const user = useLocalStorage<Record<string, any>>('userStore:user', {})
  const twoFaInProgress = useLocalStorage('userStore:twoFaInProgress', false)
  const notifications = ref<any[]>([])
  const loginInProgress = ref(false)

  const isPremiumUser = async (): Promise<boolean> => {
    if (
      user.value.subscription == 'admin' ||
      user.value.subscription == 'premium-free'
    ) {
      return true
    } else {
      if (!user.value || !user.value.id) return false

      // Check if user is properly authenticated before making API call
      const firebaseUser = useCurrentUser()
      if (!firebaseUser.value) {
        console.warn('User not authenticated, skipping premium check')
        return false
      }

      //get the subscription status from payment API
      try {
        const apiHeaders = await useApiHeaders(true)
        const { valid } = await $fetch<{ valid: boolean }>(`/api/user/${user.value.id}/valid`, {
          ...apiHeaders
        })
        return valid
      } catch (error) {
        console.warn('Failed to check premium status:', error)
        return false
      }
    }
  }

  const signedIn = computed(() => {
    const firebaseUser = useCurrentUser()
    //check if user has atleast one key in the user object

    const isLoggedIn =
      firebaseUser.value &&
      Object.keys(user.value).length > 0 &&
      user.value.twoFaInProgress !== true &&
      user.value.id != ''

    return isLoggedIn
  })

  // Plain async function — properly awaitable (replaces computed(async () => ...) anti-pattern)
  // Signal homepage nav button immediately by calling this in onMounted, not in script setup
  const checkIsAdmin = async (): Promise<boolean> => {
    const firebaseUser = useCurrentUser()
    if (!firebaseUser || !firebaseUser.value) return false
    try {
      const result = await firebaseUser.value.getIdTokenResult()
      return result?.claims?.role === 'admin'
    } catch {
      return false
    }
  }

  function resetPassword(credentials: any) {
    return resetPasswordService(credentials).then(({ data }: any) => {
      user.value = data.data
      return data.data
    })
  }

  function setMode(userData: any) {
    const newData = { id: user.value.id, ...userData }
    return updateUser(newData).then((response: any) => {
      user.value.mode = response.mode
      localStorage.setItem('sessionUser', JSON.stringify(user.value))
    })
  }

  async function setTwoFaVerified(verified: boolean) {
    await updateUser({ id: user.value.id, twoFaVerified: verified })
    user.value.twoFaVerified = verified
  }

  function addUserData(key: string, value: any) {
    user.value[key] += value
    localStorage.setItem('sessionUser', JSON.stringify(user.value))
  }

  function setPnl(pnl: number) {
    user.value.pnl += pnl
    localStorage.setItem('sessionUser', JSON.stringify(user.value))
  }

  function setTradePosition(decrease: boolean) {
    if (decrease) {
      user.value.tradePosition--
    } else {
      user.value.tradePosition++
    }
  }

  async function getUserNotifications() {
    if (notifications.value.length > 0) {
      return notifications.value
    }
    try {
      const data = await getNotifications()
      notifications.value = data as any[]
      return data
    } catch (error) {
      // Handle error silently or log it
      console.warn('Failed to get notifications:', error)
    }
    return []
  }

  function markNotificationAsRead() {
    const ids: string[] = []
    notifications.value.forEach((notification: any) => {
      notification.read = true
      ids.push(notification._id)
    })
    return markAsRead(ids, user.value.id).then(() => {})
  }

  function $reset() {
    user.value = {}
    twoFaInProgress.value = false
    notifications.value = []
    loginInProgress.value = false
  }

  return {
    user,
    notifications,
    loginInProgress,
    signedIn,
    checkIsAdmin,
    isPremiumUser,
    resetPassword,
    setMode,
    addUserData,
    setTwoFaVerified,
    setPnl,
    setTradePosition,
    getUserNotifications,
    $reset,
    markNotificationAsRead
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
